import { useTranslation } from 'next-i18next';
import { FC } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { useForm } from 'react-hook-form';
import { cx } from '@emotion/css';
import FocusTrap from 'focus-trap-react';
import { DSTextarea, DSButton } from '@stihl-design-system/components';

import { LinkOut } from '../link-out/link-out';
import { TRANSITION_DURATION } from './feedback';
import { FeedbackThumbsRating } from './feedback-thumbs-rating';
import { FeedbackStarsRating } from './feedback-stars-rating';
import { FeedbackMode as Mode, SecondaryBtn, SupportHint } from './use-feedback';

import styles from './feedback-panel.module.scss';

const MAX_CHARACTERS = 1000;

export const FeedbackPanel: FC<
  React.PropsWithChildren<{
    visible: boolean;
    feedbackMode: Mode;
    ratingValue?: number;
    onClickRating: (ratingValue: number) => void;
    onClickSubmit: ({ textRating }: { textRating: string }) => void;
    onClickClose: () => void;
    question: string;
    feedbackLabels?: [string, string];
    feedbackTextRating?: string;
    secondaryButton?: SecondaryBtn;
    supportHint?: SupportHint;
    additionalFeedbackUrl?: string;
    onReset?: () => void;
  }>
> = ({
  visible,
  feedbackMode,
  ratingValue,
  onClickRating,
  onClickClose,
  onClickSubmit,
  question,
  feedbackLabels,
  feedbackTextRating,
  secondaryButton,
  supportHint,
  additionalFeedbackUrl,
  onReset,
}) => {
  const { t } = useTranslation('shared');
  const {
    watch,
    register,
    handleSubmit,
    reset,
    formState: { isSubmitSuccessful },
  } = useForm({ mode: 'onChange' });

  const onAnimationComplete = ({ translateY = '' }) => {
    if (translateY === '100%') {
      reset({ textRating: undefined });
      onReset?.();
    }
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.dialog
          open
          aria-label={t('userFeedback_headline') as string}
          data-testid="feedbackPanel"
          className={cx(
            'fixed block bottom-0 z-50 max-h-[100vh] w-full overflow-y-auto border-t-8 border-primary-orange bg-secondary-light'
          )}
          initial={{ translateY: '100%' }}
          animate={{ translateY: '0%' }}
          exit={{ translateY: '100%' }}
          transition={{ duration: TRANSITION_DURATION / 1000, ease: 'easeInOut' }}
          onAnimationComplete={onAnimationComplete}
        >
          <FocusTrap active={visible}>
            <div
              onKeyDown={(event: React.KeyboardEvent<HTMLDivElement>) => {
                if (event.key === 'Escape') {
                  onClickClose();
                }
              }}
              className="relative mx-auto max-w-[522px] py-10 pt-6 stihl-page-container tablet:max-w-screen-2xl tablet:py-12 tablet:pt-6"
            >
              <DSButton
                iconName="cross"
                hideLabel={true}
                className="absolute right-0 top-6 h-8 w-8 cursor-pointer bg-secondary-deep p-2"
                onClick={onClickClose}
                size="small"
                data-testid="feedbackPanelClose"
              >
                {t('global_button_close')}
              </DSButton>
              {isSubmitSuccessful ? (
                <>
                  <h2 className="pr-14 text-secondary-deep stihl-text-h4-mobile tablet:stihl-text-h2-desktop">
                    {t('userFeedback_success_headline')}
                  </h2>
                  <span className="mx-2 mt-2 block h-1 w-4 bg-primary-black tablet:mx-[1px] tablet:w-5" />
                </>
              ) : (
                <form onSubmit={handleSubmit(onClickSubmit)}>
                  <h2 className="mx-2 pr-14 text-left stihl-text-h4-mobile tablet:mx-0 tablet:pr-0 tablet:stihl-text-h2-desktop">
                    {t('userFeedback_headline')}
                  </h2>
                  <span className="mx-2 mt-2 block h-1 w-4 bg-primary-black tablet:mx-[1px] tablet:w-5" />
                  <div
                    className={cx(
                      styles.panelGrid,
                      additionalFeedbackUrl ? styles.panelGridWithAdditionalContent : styles.panelGridDefaultLayout,
                      'mt-6 tablet:mt-10'
                    )}
                  >
                    <div className={cx(styles.panelGridRating, 'text-secondary-deep')}>
                      <h3 className="stihl-text-h5-mobile tablet:stihl-text-h3-desktop">{question}</h3>
                      {feedbackMode === Mode.Scale ? (
                        <FeedbackStarsRating {...{ feedbackLabels, onClickRating, ratingValue }} className="mt-4 desktop:mt-8" />
                      ) : (
                        <FeedbackThumbsRating {...{ feedbackLabels, onClickRating, ratingValue }} className="mx-auto mt-4 desktop:mt-8" />
                      )}
                    </div>
                    <div className={styles.panelGridDescription}>
                      <DSTextarea
                        id="feedback-description"
                        label={t('userFeedback_textInput_copy')}
                        data-testid="feedbackPanelTextarea"
                        className="mt-2 h-32 w-full align-top bg-primary-white !stihl-text-body-sm-reg lg:mt-6 tablet:stihl-text-body-default-reg"
                        wrapperClassName="w-full"
                        hint={feedbackTextRating}
                        maxLength={MAX_CHARACTERS}
                        {...register('textRating')}
                      />
                    </div>
                    <div
                      className={cx(styles.panelGridSubmit, 'flex flex-col space-y-4 tablet:flex-row tablet:space-x-6 tablet:space-y-0')}
                    >
                      <DSButton
                        data-testid="feedbackPanelSubmit"
                        className="w-full self-center tablet:w-auto tablet:self-end"
                        type="submit"
                        disabled={typeof ratingValue !== 'number' && !watch('textRating')}
                      >
                        {t('userFeedback_button_send')}
                      </DSButton>
                      {secondaryButton && (
                        <DSButton
                          data-testid="feedbackSecondaryBtn"
                          variant="outline"
                          className="w-full self-center tablet:w-auto tablet:self-end"
                          onClick={secondaryButton.onClick}
                          type="button"
                        >
                          {secondaryButton.text}
                        </DSButton>
                      )}
                    </div>
                    {supportHint && (
                      <div className={cx(styles.panelGridHint, 'stihl-text-body-sm-bold tablet:stihl-text-body-default-bold')}>
                        {supportHint.text}
                        <LinkOut href={supportHint.link} className="mt-4 block">
                          {t('shared_customerSupport')}
                        </LinkOut>
                      </div>
                    )}
                    {additionalFeedbackUrl && additionalFeedbackUrl !== '#' && (
                      <div
                        className={cx(styles.panelGridAdditionalContent, 'text-secondary-deep')}
                        data-testid="feedbackAdditionalContentPanel"
                      >
                        <div className="stihl-text-h5-mobile tablet:stihl-text-h3-desktop">{t('userFeedback_poolTestPersons')}</div>
                        <div className="mt-4 text-secondary-deep stihl-text-body-sm-reg lg:text-left tablet:stihl-text-body-default-reg desktop:mt-8">
                          {t('userFeedback_copyText_poolTestPersons')}
                        </div>
                        <div className="mt-4 text-secondary-deep stihl-text-body-sm-reg lg:text-left tablet:stihl-text-body-default-reg desktop:mt-8">
                          {t('userFeedback_question_poolTestPersons')}
                        </div>
                        <div className="mt-4 text-secondary-deep stihl-text-body-sm-reg lg:text-left tablet:stihl-text-body-default-reg desktop:mt-8">
                          <LinkOut href={additionalFeedbackUrl} className="mt-4 block">
                            {t('userFeedback_link_poolTestPersons')}
                          </LinkOut>
                        </div>
                      </div>
                    )}
                  </div>
                </form>
              )}
            </div>
          </FocusTrap>
        </motion.dialog>
      )}
    </AnimatePresence>
  );
};
